import { useAppStore } from 'hooks/state/useAppStore';
import { useTransitiveCacheStore } from 'hooks/state/useTransitiveCacheStore';
import { DailyDateRangePicker } from 'components/filter/input/DateRangePicker/DailyDateRangePicker';
import { INSIGHTS_MAIN_PRESET_IDS, INSIGHTS_COMPARISON_PRESET_IDS } from './presets';

export interface InsightsDateRangePickerProps {
  caption: string;
  emptyMessage?: string;
  disabled?: boolean;
}

/**
 * Main date range picker for Insights module.
 * Connected to the app store for global date range state.
 * Automatically recalculates comparison when main range changes.
 */
// eslint-disable-next-line react-refresh/only-export-components
function Default({
  caption,
  emptyMessage = 'datepicker.empty.default',
  disabled = false,
}: InsightsDateRangePickerProps) {
  const { dateRange, setDateRange } = useAppStore();

  return (
    <DailyDateRangePicker
      value={dateRange}
      onChange={setDateRange}
      presetIds={INSIGHTS_MAIN_PRESET_IDS}
      comparisonPresetIds={INSIGHTS_COMPARISON_PRESET_IDS}
      caption={caption}
      emptyMessage={emptyMessage}
      disabled={disabled}
      hideButtons
    />
  );
}

/**
 * Comparison date range picker for Insights module.
 * Connected to the app store for global date range state.
 * Edits the comparison portion of the date range.
 */
// eslint-disable-next-line react-refresh/only-export-components
function Comparison({
  caption,
  emptyMessage = 'datepicker.empty.default',
  disabled = undefined,
}: InsightsDateRangePickerProps) {
  const { dateRange, setDateRange } = useAppStore();
  const { disabledFilters } = useTransitiveCacheStore();

  const isDisabled = disabled ?? disabledFilters?.includes('comparisonDateRange') ?? false;

  return (
    <DailyDateRangePicker
      value={dateRange}
      onChange={setDateRange}
      presetIds={INSIGHTS_COMPARISON_PRESET_IDS}
      isComparison
      caption={caption}
      emptyMessage={emptyMessage}
      disabled={isDisabled}
      hideButtons
    />
  );
}

/**
 * Compound component for Insights date range pickers.
 *
 * @example
 * <InsightsDateRangePicker.Default caption="filter.key.selectedDateRange" />
 * <InsightsDateRangePicker.Comparison caption="filter.key.selectedDateRangeComparison" />
 */
export const InsightsDateRangePicker = {
  Default,
  Comparison,
};
